import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpId, postEntities } from '@onefootprint/fetch-dashboard';
import { IcoArrowTopRight16, IcoPencil16 } from '@onefootprint/icons';
import type { ApiError, VaultData, VaultKind } from '@onefootprint/request-types/dashboard';
import { Button, CodeInline, Form, LinkButton, SegmentedControl } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import Link from 'next/link';
import { type Ref, useImperativeHandle } from 'react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useSession from '@/hooks/use-session';
import { DEFAULT_BOOTSTRAP_DATA, DEFAULT_BUSINESS_BOOTSTRAP_DATA } from '../../../../test-mode.constants';
import type { EntityCreatorFormValues, SaveEntityInput } from '../../onboarding.types';
import { parseJson5Object } from '../../onboarding.utils';
import { ApiErrorAlert } from '../api-error-alert';
import { StepCard } from '../step-card/step-card';
import { VerifiedMatchTip } from '../verified-match-tip/verified-match-tip';

// An id carrying its kind's prefix is a real fp_id; anything else is looked up as an external id,
// and the other kind's prefix is rejected before it reaches the API.
const FP_ID_PREFIX: Record<VaultKind, string> = {
  business: 'fp_bid',
  person: 'fp_id_'
};
type EntityCreatorSectionProps = {
  ref?: Ref<EntityCreatorHandle>;
  entityKind: VaultKind;
  savedFpId: string | null;
  setSavedFpId: (fpId: string) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  showVerifiedMatchTip?: boolean;
};
export type EntityCreatorHandle = {
  submit: () => void;
};
export const EntityCreatorSection = t0 => {
  const $0 = _c(69);
  const {
    ref,
    entityKind,
    savedFpId,
    setSavedFpId,
    isEditing,
    setIsEditing,
    showVerifiedMatchTip
  } = t0;
  let t1;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "testMode.runMode.entityCreator"
    };
    $0[0] = t1;
  } else {
    t1 = $0[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($0[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "testMode.runMode"
    };
    $0[1] = t2;
  } else {
    t2 = $0[1];
  }
  const {
    t: tRunMode
  } = useTranslation("playbook-builder", t2);
  const {
    authHeaders
  } = useSession();
  const isBusiness = entityKind === "business";
  const t3 = isBusiness ? DEFAULT_BUSINESS_BOOTSTRAP_DATA : DEFAULT_BOOTSTRAP_DATA;
  let t4;
  if ($0[2] !== t3) {
    t4 = {
      defaultValues: {
        mode: "create",
        vaultData: t3,
        existingId: ""
      }
    };
    $0[2] = t3;
    $0[3] = t4;
  } else {
    t4 = $0[3];
  }
  const form = useForm(t4);
  let t5;
  if ($0[4] !== form.control) {
    t5 = {
      name: "mode",
      control: form.control
    };
    $0[4] = form.control;
    $0[5] = t5;
  } else {
    t5 = $0[5];
  }
  const {
    field: modeField
  } = useController(t5);
  let t6;
  if ($0[6] !== authHeaders || $0[7] !== entityKind || $0[8] !== t) {
    t6 = async input => {
      if (input.kind === "existing") {
        let id = input.id;
        if (!input.id.startsWith(FP_ID_PREFIX[entityKind])) {
          id = `ext_id:${input.id}`;
        }
        const {
          data
        } = await getEntitiesByFpId({
          path: {
            fpId: id
          },
          headers: authHeaders
        });
        if (data?.kind !== entityKind) {
          throw new Error(t($ => $[entityKind].notFound));
        }
        return data.id;
      }
      const {
        data: data_0
      } = await postEntities({
        body: {
          entityKind,
          data: input.vaultData as VaultData
        },
        headers: authHeaders
      });
      return data_0.fpId;
    };
    $0[6] = authHeaders;
    $0[7] = entityKind;
    $0[8] = t;
    $0[9] = t6;
  } else {
    t6 = $0[9];
  }
  let t7;
  if ($0[10] !== form || $0[11] !== setIsEditing || $0[12] !== setSavedFpId) {
    t7 = (fpId, input_0) => {
      setSavedFpId(fpId);
      setIsEditing(false);
      if (input_0.kind === "create") {
        form.setValue("existingId", "");
      }
    };
    $0[10] = form;
    $0[11] = setIsEditing;
    $0[12] = setSavedFpId;
    $0[13] = t7;
  } else {
    t7 = $0[13];
  }
  let t8;
  if ($0[14] !== t6 || $0[15] !== t7) {
    t8 = {
      mutationFn: t6,
      onSuccess: t7
    };
    $0[14] = t6;
    $0[15] = t7;
    $0[16] = t8;
  } else {
    t8 = $0[16];
  }
  const saveEntityMutation = useMutation(t8);
  let t9;
  if ($0[17] !== form || $0[18] !== saveEntityMutation || $0[19] !== tRunMode) {
    t9 = values => {
      let input_1;
      if (values.mode === "existing") {
        input_1 = {
          kind: "existing",
          id: (values.existingId ?? "").trim()
        };
      } else {
        try {
          input_1 = {
            kind: "create",
            vaultData: parseJson5Object(values.vaultData)
          };
        } catch {
          form.setError("vaultData", {
            message: tRunMode(_temp)
          });
          return;
        }
      }
      saveEntityMutation.mutate(input_1);
    };
    $0[17] = form;
    $0[18] = saveEntityMutation;
    $0[19] = tRunMode;
    $0[20] = t9;
  } else {
    t9 = $0[20];
  }
  const handleSubmit = t9;
  let t10;
  if ($0[21] !== saveEntityMutation || $0[22] !== setIsEditing) {
    t10 = () => {
      saveEntityMutation.reset();
      setIsEditing(true);
    };
    $0[21] = saveEntityMutation;
    $0[22] = setIsEditing;
    $0[23] = t10;
  } else {
    t10 = $0[23];
  }
  const handleEdit = t10;
  let t11;
  if ($0[24] !== saveEntityMutation || $0[25] !== setIsEditing) {
    t11 = () => {
      saveEntityMutation.reset();
      setIsEditing(false);
    };
    $0[24] = saveEntityMutation;
    $0[25] = setIsEditing;
    $0[26] = t11;
  } else {
    t11 = $0[26];
  }
  const handleCancel = t11;
  const collapsed = !!savedFpId && !isEditing;
  const isSaving = saveEntityMutation.isPending;
  const isReplacingEntity = !!savedFpId && modeField.value !== "existing";
  let t12;
  if ($0[27] !== form || $0[28] !== handleSubmit) {
    t12 = form.handleSubmit(handleSubmit);
    $0[27] = form;
    $0[28] = handleSubmit;
    $0[29] = t12;
  } else {
    t12 = $0[29];
  }
  const submit = t12;
  let t13;
  if ($0[30] !== isSaving || $0[31] !== submit) {
    t13 = () => ({
      submit: () => {
        if (!isSaving) {
          submit();
        }
      }
    });
    $0[30] = isSaving;
    $0[31] = submit;
    $0[32] = t13;
  } else {
    t13 = $0[32];
  }
  useImperativeHandle(ref, t13);
  let t14;
  if ($0[33] !== collapsed || $0[34] !== entityKind || $0[35] !== handleEdit || $0[36] !== isBusiness || $0[37] !== savedFpId || $0[38] !== t) {
    t14 = collapsed ? <><CodeInline size="compact">{savedFpId}</CodeInline><LinkButton href={`/${isBusiness ? "businesses" : "users"}/${savedFpId}`} target="_blank" iconComponent={IcoArrowTopRight16}>{t($_1 => $_1[entityKind].view)}</LinkButton><LinkButton iconComponent={IcoPencil16} onClick={handleEdit}>{t(_temp2)}</LinkButton></> : null;
    $0[33] = collapsed;
    $0[34] = entityKind;
    $0[35] = handleEdit;
    $0[36] = isBusiness;
    $0[37] = savedFpId;
    $0[38] = t;
    $0[39] = t14;
  } else {
    t14 = $0[39];
  }
  const summary = t14;
  const t15 = collapsed ? "complete" : "active";
  let t16;
  if ($0[40] !== entityKind) {
    t16 = $_3 => $_3[entityKind].title;
    $0[40] = entityKind;
    $0[41] = t16;
  } else {
    t16 = $0[41];
  }
  let t17;
  if ($0[42] !== t || $0[43] !== t16) {
    t17 = t(t16);
    $0[42] = t;
    $0[43] = t16;
    $0[44] = t17;
  } else {
    t17 = $0[44];
  }
  const t18 = !collapsed;
  let t19;
  if ($0[45] !== collapsed || $0[46] !== entityKind || $0[47] !== handleCancel || $0[48] !== isBusiness || $0[49] !== isReplacingEntity || $0[50] !== isSaving || $0[51] !== modeField || $0[52] !== saveEntityMutation.error || $0[53] !== savedFpId || $0[54] !== showVerifiedMatchTip || $0[55] !== t) {
    t19 = collapsed ? null : <><SegmentedControl aria-label={t($_4 => $_4[entityKind].modeLabel)} size="compact" options={[{
        value: "create",
        label: t($_5 => $_5[entityKind].modeCreate)
      }, {
        value: "existing",
        label: t($_6 => $_6[entityKind].modeExisting)
      }]} value={modeField.value === "existing" ? "existing" : "create"} onChange={modeField.onChange} />{modeField.value === "existing" ? <Form.Input name="existingId" size="compact" placeholder={t($_7 => $_7[entityKind].existingIdPlaceholder)} rules={{
        required: t(_temp3),
        validate: value => {
          if (typeof value !== "string") {
            return true;
          }
          const otherKind = isBusiness ? "person" : "business";
          return value.trim().startsWith(FP_ID_PREFIX[otherKind]) ? t($_9 => $_9[entityKind].wrongKind) : true;
        }
      }} /> : <><p className="text-body-2 text-tertiary"><Trans ns="playbook-builder" i18nKey={$_10 => $_10.testMode.runMode.entityCreator[entityKind].vaultDataHint} components={{
            link: <Link href="https://docs.onefootprint.com/articles/vault/fields" target="_blank" rel="noreferrer" className="text-accent hover:underline">{t(_temp4)}</Link>
          }} /></p>{showVerifiedMatchTip && <VerifiedMatchTip />}<Form.JsonEditor name="vaultData" allowTrailingCommas={true} /></>}{saveEntityMutation.error && <ApiErrorAlert error={saveEntityMutation.error} />}<div className="flex justify-end gap-2">{savedFpId && <Button variant="secondary" size="compact" onClick={handleCancel}>{t(_temp5)}</Button>}<Button type="submit" form="onboarding-entity-form" size="compact" loading={isSaving} disabled={isSaving}>{isReplacingEntity ? t($_13 => $_13[entityKind].createAnother) : t($_14 => $_14[entityKind].continue)}</Button></div></>;
    $0[45] = collapsed;
    $0[46] = entityKind;
    $0[47] = handleCancel;
    $0[48] = isBusiness;
    $0[49] = isReplacingEntity;
    $0[50] = isSaving;
    $0[51] = modeField;
    $0[52] = saveEntityMutation.error;
    $0[53] = savedFpId;
    $0[54] = showVerifiedMatchTip;
    $0[55] = t;
    $0[56] = t19;
  } else {
    t19 = $0[56];
  }
  let t20;
  if ($0[57] !== summary || $0[58] !== t15 || $0[59] !== t17 || $0[60] !== t18 || $0[61] !== t19) {
    t20 = <StepCard step={1} state={t15} title={t17} aside={summary} focusOnExpand={t18}>{t19}</StepCard>;
    $0[57] = summary;
    $0[58] = t15;
    $0[59] = t17;
    $0[60] = t18;
    $0[61] = t19;
    $0[62] = t20;
  } else {
    t20 = $0[62];
  }
  let t21;
  if ($0[63] !== submit || $0[64] !== t20) {
    t21 = <form id="onboarding-entity-form" onSubmit={submit}>{t20}</form>;
    $0[63] = submit;
    $0[64] = t20;
    $0[65] = t21;
  } else {
    t21 = $0[65];
  }
  let t22;
  if ($0[66] !== form || $0[67] !== t21) {
    t22 = <FormProvider {...form}>{t21}</FormProvider>;
    $0[66] = form;
    $0[67] = t21;
    $0[68] = t22;
  } else {
    t22 = $0[68];
  }
  return t22;
};
function _temp($_0) {
  return $_0.invalidJson;
}
function _temp2($_2) {
  return $_2.edit;
}
function _temp3($_8) {
  return $_8.required;
}
function _temp4($_11) {
  return $_11.here;
}
function _temp5($_12) {
  return $_12.cancel;
}